import * as THREE from 'three';
import Portal from './Portal.js';
import { FontLoader } from 'three/examples/jsm/loaders/FontLoader.js';
import { TextGeometry } from 'three/examples/jsm/geometries/TextGeometry.js';

export default class PortalManager {
    constructor(scene, bus) {
        this.scene = scene;
        this.bus = bus;
        this.portals = [];
        
        // Initialize portals if URL parameter is present
        if (new URLSearchParams(window.location.search).get('portal')) {
            this.createStartPortal();
        }
        this.createExitPortal();
        
        // Start checking for collisions
        this.checkCollisions();
    }
    
    createStartPortal() {
        const startPortal = new Portal(
            this.scene,
            new THREE.Vector3(0, 1, -150), // Positioned further ahead but still visible
            0xff0000, // Red color 
            true 
        );
        this.portals.push(startPortal);
    }
    
    createExitPortal() {
        const exitPortal = new Portal(
            this.scene,
            new THREE.Vector3(0, 1, 150), // Portal position
            0x00ff00 // Green color
        );
        this.portals.push(exitPortal);
    
        const loader = new FontLoader();
        loader.load('https://threejs.org/examples/fonts/helvetiker_regular.typeface.json', (font) => {
            const textGeometry = new TextGeometry('Vibeverse Portal', {
                font: font,
                size: 5,
                height: 1,
            });
    
            textGeometry.computeBoundingBox();
            const boundingBox = textGeometry.boundingBox;
    
            // Center the text geometry
            const centerX = (boundingBox.max.x + boundingBox.min.x) / 2;
            const centerY = (boundingBox.max.y + boundingBox.min.y) / 2;
            textGeometry.translate(-centerX, -centerY, 0);
    
            const textMaterial = new THREE.MeshBasicMaterial({ color: 'magenta' });
            const textMesh = new THREE.Mesh(textGeometry, textMaterial);
    
            // Position text above the portal (adjust Y offset as needed)
            textMesh.position.set(0, 23, 150.1); // Slightly in front of the portal
            textMesh.rotation.y = Math.PI; // Face the same direction as the portal
            this.scene.add(textMesh);
        });
    }
    
    checkCollisions() {
        // Check for collisions with each portal
        this.portals.forEach(portal => {
            if (portal.checkCollision(this.bus.mesh)) {
                if (portal.isStart) {
                    // Handle start portal collision
                    const urlParams = new URLSearchParams(window.location.search);
                    const refUrl = urlParams.get('ref');
                    if (refUrl) {
                        // Add https if not present and include query params
                        let url = refUrl;
                        if (!url.startsWith('http://') && !url.startsWith('https://')) {
                            url = 'https://' + url;
                        }
                        const currentParams = new URLSearchParams(window.location.search);
                        const newParams = new URLSearchParams();
                        for (const [key, value] of currentParams) {
                            if (key !== 'ref') { // Skip ref param since it's in the base URL
                                newParams.append(key, value);
                            }
                        }
                        const paramString = newParams.toString();
                        window.location.href = url + (paramString ? '?' + paramString : '');
                    }
                } else {
                    // Handle exit portal collision
                    const currentParams = new URLSearchParams(window.location.search);
                    const newParams = new URLSearchParams();
                    newParams.append('portal', true);
                    // Add any additional params from current URL
                    for (const [key, value] of currentParams) {
                        newParams.append(key, value);
                    }
                    const paramString = newParams.toString();
                    const nextPage = 'https://portal.pieter.com' + (paramString ? '?' + paramString : '');

                    // Create hidden iframe to preload next page
                    if (!document.getElementById('preloadFrame')) {
                        const iframe = document.createElement('iframe');
                        iframe.id = 'preloadFrame';
                        iframe.style.display = 'none';
                        iframe.src = nextPage;
                        document.body.appendChild(iframe);
                    }

                    // Redirect to next page
                    window.location.href = nextPage;
                }
            }
        });
        
        requestAnimationFrame(() => this.checkCollisions());
    }
    
    dispose() {
        // Clean up all portals
        this.portals.forEach(portal => portal.dispose());
        this.portals = [];
    }
}